import React from 'react';

import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  colors,
  Heading,
  HStack,
  Primitive,
  VStack,
} from '@rtkwlf/fenrir-react';
import { faExpandArrows } from '@rtkwlf/pro-solid-svg-icons/faExpandArrows';
import { faTimes } from '@rtkwlf/pro-solid-svg-icons/faTimes';
import type { Ticket, TicketWithComments } from '@rtkwlf/rendall';
import type { QueryKey } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { generatePath, useNavigate } from 'react-router-dom';

import { useRendallApi } from '@apis/rendall/use-rendall-api';
import { GeneralWrapper } from '@packages/components/generalWrapper/GeneralWrapper';
import { useSelectedOrganization } from '@packages/legacy/organization-context';
import { ticketKeys } from '@packages/utils/queryKeyFactories/tickets';
import { TICKET_DETAILS_ROUTE } from 'ae-constants/routes.constants';

import { TicketAction } from '../../ticketAction/TicketAction';
import { TicketAttachments } from '../ticketComments/ticketAttachments/TicketAttachments';
import { TicketComments } from '../ticketComments/TicketComments';
import { DetailsValue } from '../ticketDescription/DetailsValue';
import { EMPTY_TICKET } from '../TicketDetails';
import {
  csvType,
  TicketEvidenceNavigator,
} from '../ticketEvidenceNavigator/TicketEvidenceNavigator';

import { FollowUpButton } from './FollowUpButton';
import { TicketInfoAccordion } from './ticketInfoAccordion/TicketInfoAccordion';

const Separator = styled.hr({
  border: `1px solid rgb(${colors.border.neutral.secondary})`,
  margin: '0',
  width: '99.5%',
});

export type TicketDetailsReadingPaneProps = {
  ticket: Ticket;
  close: () => void;
  ticketListQueryKey?: QueryKey;
};

export const TicketDetailsReadingPane: React.FC<
  TicketDetailsReadingPaneProps
> = ({ ticket, close, ticketListQueryKey }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const organization = useSelectedOrganization();
  const api = useRendallApi();

  const ticketId = ticket.id.toString();

  const {
    isPending,
    isFetching,
    isPlaceholderData,
    isError,
    data: ticketWithComments = EMPTY_TICKET,
  } = useQuery<TicketWithComments>({
    queryFn: () =>
      api.getTicketV2({
        organizationId: organization.id,
        ticketId,
      }),
    queryKey: ticketKeys.detail(organization.id, ticketId),
  });

  const {
    subject,
    description,
    attachments = [],
    comments = [],
  } = ticketWithComments;

  const csvAttachments = attachments.filter(({ name = '' }) =>
    name.includes(csvType),
  );

  const handleOpenTicketDetails = () => {
    const url = generatePath(TICKET_DETAILS_ROUTE, {
      ticketId,
    });
    navigate(url);
  };

  return (
    <GeneralWrapper
      isError={isError}
      isLoading={isPending || (isFetching && isPlaceholderData)}
      css={css({
        flex: 1,
        maxHeight: '75vh',
        overflowY: 'auto',
      })}
    >
      <VStack
        data-testid={`readingPane-${ticketId}`}
        backgroundColor="widgetSecondary"
        borderColor="neutralDefault"
        borderWidth="default"
        borderStyle="solid"
        paddingX="medium"
        yAlign="top"
      >
        <HStack
          xAlign="between"
          width="full"
          marginBottom="small"
          marginTop="medium"
        >
          <Heading level="3">{subject}</Heading>
          <HStack
            // eslint-disable-next-line @emotion/syntax-preference -- FIXME
            css={css`
              @media screen and (max-width: 1280px) {
                flex-wrap: wrap;
              }
            `}
          >
            <Button
              variant="tertiary"
              iconLeft={<FontAwesomeIcon icon={faTimes} />}
              onClick={close}
            >
              {t('ticket:readingPane.hide')}
            </Button>
            <Button
              variant="tertiary"
              iconLeft={<FontAwesomeIcon icon={faExpandArrows} />}
              onClick={handleOpenTicketDetails}
            >
              {t('ui:fullScreen')}
            </Button>
          </HStack>
        </HStack>
        <TicketInfoAccordion ticket={ticketWithComments as unknown as Ticket} />
        <VStack width="full" yAlign="top" overflow="auto">
          <Heading level="4" marginTop="medium">
            {t('ticket:details.details')}
          </Heading>
          <DetailsValue value={description} type="markdown" />
          {Boolean(attachments.length) && (
            <Primitive.div>
              <Heading level="4" marginBottom="medium">
                {t('ticket:details.attachments')}
                <Primitive.span marginLeft="xsmall">{`(${attachments.length})`}</Primitive.span>
              </Heading>
              <TicketAttachments attachments={attachments} />
            </Primitive.div>
          )}
          <TicketEvidenceNavigator
            ticketId={ticketId}
            attachments={csvAttachments}
          />
          <Separator />
          <FollowUpButton ticket={ticketWithComments} />
          <TicketComments comments={comments} />
          <TicketAction
            ticket={ticketWithComments}
            ticketListQueryKey={ticketListQueryKey}
          />
        </VStack>
        <Primitive.div marginBottom="medium" />
      </VStack>
    </GeneralWrapper>
  );
};
